import { RawData } from "react-spreadsheet-import/types/types";
import { PhoneType } from "@/enums/contacts";
import { SupportedCountries } from "@/enums/internationalization";
import { ContactUpload, ContactUploadFailureRow } from "@/types/contacts";

export type UploadContactFieldType = {
  label: string;
  key: string;
  alternateMatches: string[];
  fieldType: {
    type: "input";
  };
  example?: string;
  validations?: {
    rule: "required" | "unique" | "regex";
    value?: string | RegExp;
    errorMessage: string;
    level?: "info" | "warning" | "error";
  }[];
};

export type UploadContactPhoneFieldMapping = {
  header: string;
  type: "phone" | "home" | "work" | "fax";
  index: number;
  key: string;
};

type UnknownHeader = {
  label: string;
  index: number;
};

const normalizeHeader = (value: string): string => value.trim().toLowerCase();

type BuildRowsParams = {
  validRows: Record<string, any>[];
  fields: UploadContactFieldType[];
  defaultFields: UploadContactFieldType[];
  phoneMappings: UploadContactPhoneFieldMapping[];
  country: SupportedCountries;
  normalizePhoneNumber: (phone: string, country: SupportedCountries) => string;
  buildPreUploadFailureRow: (
    rawLead: Record<string, unknown>,
    phoneFieldKeys: string[],
    reason: string,
    country: SupportedCountries
  ) => ContactUploadFailureRow;
  preUploadFailureReason: string;
  isCustomVariableValid: (key: string) => boolean;
};

const getPhoneTypeFromFieldLabel = (
  label: string
): UploadContactPhoneFieldMapping["type"] => {
  if (label.includes("🏠")) return "home";
  if (label.includes("🏢")) return "work";
  if (label.includes("📠")) return "fax";
  return "phone";
};

export const buildPhoneMappingsFromFields = (
  fields: UploadContactFieldType[]
): UploadContactPhoneFieldMapping[] =>
  fields
    .filter((field) =>
      field.validations?.some((validation) => validation.rule === "regex")
    )
    .map((field) => ({
      header: field.key,
      type: getPhoneTypeFromFieldLabel(field.label),
      index: 1,
      key: field.key,
    }));

export const buildFallbackPhoneMappings = (
  uploadDataAll: Record<string, any>[] | undefined,
  groupPhoneFields: (
    headers: string[],
    t: (key: string) => string
  ) => {
    phoneFields: {
      header: string;
      type: string;
      index: number;
      key: string;
    }[];
  },
  t: (key: string) => string
): UploadContactPhoneFieldMapping[] => {
  if (!uploadDataAll || uploadDataAll.length === 0) {
    return [];
  }

  const firstRow = uploadDataAll[0];
  const headers = Object.keys(firstRow);
  const { phoneFields } = groupPhoneFields(headers, t);
  return phoneFields.map((phoneField) => ({
    ...phoneField,
    type: phoneField.type as UploadContactPhoneFieldMapping["type"],
  }));
};

export const extractCustomDataFromLead = ({
  lead,
  fields,
  defaultFields,
  phoneMappings,
  isCustomVariableValid,
}: {
  lead: Record<string, any>;
  fields: UploadContactFieldType[];
  defaultFields: UploadContactFieldType[];
  phoneMappings: UploadContactPhoneFieldMapping[];
  isCustomVariableValid: (key: string) => boolean;
}): Record<string, any> =>
  Object.keys(lead)
    .filter((key) => {
      const cleanKey = key.endsWith("_") ? key.slice(0, -1) : key;
      const isPhoneField = phoneMappings.some((pf) => pf.header === cleanKey);
      const hasCustomFieldConfig = fields.some(
        (field) =>
          field.key === key &&
          !field.validations?.some((validation) => validation.rule === "regex")
      );

      if (hasCustomFieldConfig) return true;
      if (isPhoneField) return false;

      const looksLikePhone = /^.*?(phone|mobile|cell|tel|telephone).*$/i.test(
        cleanKey
      );
      if (looksLikePhone) return false;

      return true;
    })
    .reduce((acc: Record<string, any>, key) => {
      if (
        !defaultFields.some((field) => field.key === key) &&
        isCustomVariableValid(key)
      ) {
        const cleanKey = key.endsWith("_") ? key.slice(0, -1) : key;
        acc[cleanKey] = lead[key];
      }
      return acc;
    }, {});

export const collectNormalizedPhoneNumbers = ({
  lead,
  phoneMappings,
  country,
  normalizePhoneNumber,
}: {
  lead: Record<string, any>;
  phoneMappings: UploadContactPhoneFieldMapping[];
  country: SupportedCountries;
  normalizePhoneNumber: (phone: string, country: SupportedCountries) => string;
}): Array<{ number: string; type: PhoneType }> => {
  let phoneNumbers: Array<{ number: string; type: PhoneType }> = [];

  phoneMappings.forEach((phoneField) => {
    const phoneValue = lead[phoneField.header];
    if (phoneValue?.trim()) {
      let phoneType = PhoneType.Mobile;
      if (phoneField.type === "work") phoneType = PhoneType.Work;
      else if (phoneField.type === "home") phoneType = PhoneType.Home;
      else if (phoneField.type === "fax") phoneType = PhoneType.Fax;

      phoneNumbers.push({
        number: normalizePhoneNumber(phoneValue, country),
        type: phoneType,
      });
    }
  });

  if (lead.phone) {
    phoneNumbers.push({
      number: normalizePhoneNumber(lead.phone, country),
      type: PhoneType.Mobile,
    });
  }

  phoneNumbers = phoneNumbers.filter(
    (phone, index, self) =>
      phone.number && index === self.findIndex((candidate) => candidate.number === phone.number)
  );

  return phoneNumbers;
};

export const mapValidRowsToContactUploads = ({
  validRows,
  fields,
  defaultFields,
  phoneMappings,
  country,
  normalizePhoneNumber,
  buildPreUploadFailureRow,
  preUploadFailureReason,
  isCustomVariableValid,
}: BuildRowsParams): {
  leads: ContactUpload[];
  preUploadFailures: ContactUploadFailureRow[];
} => {
  const phoneFieldKeys = Array.from(new Set(phoneMappings.map((field) => field.header)));
  const preUploadFailures: ContactUploadFailureRow[] = [];

  const leads: ContactUpload[] = validRows
    .map((lead) => {
      const customData = extractCustomDataFromLead({
        lead,
        fields,
        defaultFields,
        phoneMappings,
        isCustomVariableValid,
      });

      const phoneNumbers = collectNormalizedPhoneNumbers({
        lead,
        phoneMappings,
        country,
        normalizePhoneNumber,
      });

      const result: ContactUpload = {
        firstName: (lead.first_name || "N/A").trim(),
        lastName: (lead.last_name || "").trim(),
        businessName: (lead.business_name || "").trim(),
        phoneNumbers,
        emails: lead.email ? [(lead.email || "").trim()] : [],
        additionalData: {
          address_line_1: (lead.address_line_1 || "").trim(),
          address_line_2: (lead.address_line_2 || "").trim(),
          city: (lead.city || "").trim(),
          state: (lead.state || "").trim(),
          zip: (lead.zip || "").trim(),
          lead_source: (lead.lead_source || "").trim(),
          ...customData,
        },
      };

      if (result.phoneNumbers.length === 0) {
        preUploadFailures.push(
          buildPreUploadFailureRow(
            lead,
            phoneFieldKeys,
            preUploadFailureReason,
            country
          )
        );
        return null;
      }

      return result;
    })
    .filter((lead): lead is ContactUpload => lead !== null);

  return {
    leads,
    preUploadFailures,
  };
};

export const getUnknownHeaders = (
  headerValues: RawData,
  defaultFields: UploadContactFieldType[]
): UnknownHeader[] => {
  const possibleKnownHeaders = new Set(
    defaultFields.flatMap((field) =>
      [field.key, ...field.alternateMatches].map((header) => normalizeHeader(header))
    )
  );

  return headerValues
    .map((header, index) => ({ label: header?.trim() || "", index }))
    .filter(
      (header) => !possibleKnownHeaders.has(normalizeHeader(header.label || ""))
    );
};

const buildPhoneFieldLabel = (
  phoneField: { header: string; type: string; index: number },
  t: (key: string) => string
): string => {
  const getPhoneConfig = () => {
    if (phoneField.type === "phone") {
      return {
        emoji: /mobile|cell/i.test(phoneField.header) ? "📱" : "📞",
        baseHeader: "phone",
        indexedPattern: /^phone\d+$/i,
        translationKey: "phone",
      };
    }
    if (phoneField.type === "work") {
      return {
        emoji: "🏢",
        baseHeader: "work",
        indexedPattern: /^work\d+$/i,
        translationKey: "work",
      };
    }
    if (phoneField.type === "home") {
      return {
        emoji: "🏠",
        baseHeader: "home",
        indexedPattern: /^home\d+$/i,
        translationKey: "home",
      };
    }
    return {
      emoji: "📠",
      baseHeader: "fax",
      indexedPattern: /^fax\d+$/i,
      translationKey: "fax",
    };
  };

  const config = getPhoneConfig();
  const hasIndexedMatch = config.indexedPattern.exec(phoneField.header) !== null;

  if (phoneField.header !== config.baseHeader && !hasIndexedMatch) {
    return `${config.emoji} ${phoneField.header}`;
  }

  return phoneField.index > 1
    ? `${config.emoji} ${t(config.translationKey)} ${phoneField.index}`
    : `${config.emoji} ${t(config.translationKey)}`;
};

export const buildPhoneFieldMappingsForHeaderSelect = ({
  phoneFields,
  unknownHeaders,
  data,
  t,
  phoneValidationRegex,
  phoneNotValidMessage,
}: {
  phoneFields: Array<{ header: string; type: string; index: number }>;
  unknownHeaders: UnknownHeader[];
  data: RawData[];
  t: (key: string) => string;
  phoneValidationRegex: RegExp;
  phoneNotValidMessage: string;
}): UploadContactFieldType[] =>
  phoneFields.map((phoneField) => {
    const headerIndex = unknownHeaders.findIndex((header) => header.label === phoneField.header);
    const header = unknownHeaders[headerIndex];

    return {
      label: buildPhoneFieldLabel(phoneField, t),
      key: phoneField.header,
      alternateMatches: [phoneField.header],
      fieldType: {
        type: "input",
      },
      example: data[0]?.[header.index] || "+1 (408) 555-0123",
      validations: [
        {
          rule: "regex",
          value: phoneValidationRegex,
          errorMessage: phoneNotValidMessage,
          level: "warning",
        },
      ],
    };
  });

export const buildPhoneCustomFieldMappings = ({
  phoneFields,
  unknownHeaders,
  data,
}: {
  phoneFields: Array<{ header: string; type: string; index: number }>;
  unknownHeaders: UnknownHeader[];
  data: RawData[];
}): UploadContactFieldType[] =>
  phoneFields.map((phoneField) => {
    const headerIndex = unknownHeaders.findIndex((header) => header.label === phoneField.header);
    const header = unknownHeaders[headerIndex];

    return {
      label: phoneField.header,
      key: `${phoneField.header}_`,
      alternateMatches: [phoneField.header],
      fieldType: {
        type: "input",
      },
      example: data[0]?.[header.index] || "",
    };
  });

export const buildNonPhoneFieldMappings = ({
  nonPhoneFields,
  unknownHeaders,
  data,
  getVariablesAllowed,
}: {
  nonPhoneFields: string[];
  unknownHeaders: UnknownHeader[];
  data: RawData[];
  getVariablesAllowed: (labels: string[]) => string[];
}): UploadContactFieldType[] => {
  // Case-insensitive first spelling wins — calling getVariablesAllowed one
  // header at a time never sees a HIPAA_Contact / HIPAA_contact pair (GAIL-1110).
  const seenNormalized = new Set<string>();

  return nonPhoneFields
    .map((fieldLabel) => {
      const header = unknownHeaders.find((candidate) => candidate.label === fieldLabel);
      if (!header) return null;

      const labels = getVariablesAllowed([header.label]);
      if (labels.length === 0) return null;

      const label = labels[0];
      const normalized = label.toLowerCase();
      if (seenNormalized.has(normalized)) return null;
      seenNormalized.add(normalized);

      return {
        label,
        key: label,
        alternateMatches: [header.label],
        fieldType: {
          type: "input" as const,
        },
        example: data[0]?.[header.index] || "",
      };
    })
    .filter((field) => field !== null) as UploadContactFieldType[];
};
